import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { COLOR_SYSTEM } from '../../design/design-system';
import Button from '../../atoms/Button';
import Icon from '../../atoms/Icon';
import InfoIconButton from '../../atoms/InfoIconButton';
import { PrintOptions } from './index';
import { MentionedAddition } from '../../../utils/prescriptionPrintScope';
import { EndedDrugBadge } from '../../molecules/PrescriptionTableDisplay';

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;

  /* 약사 화면은 감싸는 SidePanel(index.tsx)이 인쇄에서 숨기지만, 탑재뷰는 그 래퍼가 없다.
     패널 자체를 인쇄에서 숨겨 어느 소비자에서도 인쇄물에 패널이 안 남게 한다(계획 item 11 · AC-34). */
  @media print {
    display: none;
  }
`;

const PanelHeader = styled.div`
  padding: 20px 20px 16px 20px;
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
`;

const Title = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
  margin: 0 0 12px 0;
`;

const HeaderDescription = styled.p`
  font-size: 12px;
  line-height: 1.5;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
  margin: 0;
`;

const PresetSection = styled.div`
  background-color: ${COLOR_SYSTEM.get("Gray")?.[20]};
  border-radius: 8px;
  padding: 14px;
  margin: 0;
`;

const SectionLabel = styled.h3<{ $variant?: 'preset' | 'custom' }>`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.$variant === 'preset'
    ? COLOR_SYSTEM.get("Gray")?.[700]
    : COLOR_SYSTEM.get("Gray")?.[800]};
  margin: 0 0 10px 0;
`;

const PresetButtonGroup = styled.div<{ $vertical?: boolean }>`
  display: flex;
  /* 파트너 접이식 패널은 폭 240px이라 가로 2열이면 라벨이 잘린다 → 세로 배치.
     약사 화면(비접이식)은 넓은 SidePanel(320px)이라 가로 2열 유지(AC-35 회귀). */
  flex-direction: ${(props) => (props.$vertical ? "column" : "row")};
  gap: 8px;
  margin: 0;
`;

const PresetButton = styled(Button)`
  font-size: 12px;
  flex: 1;
  white-space: nowrap;
  justify-content: center;
`;

const PanelBody = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
`;

const OptionSection = styled.div`
  margin-top: 20px;
  margin-bottom: 24px;
`;

const OptionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
  margin: 0 0 12px 0;
`;

const OptionGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CheckboxLabel = styled.label<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  font-size: 14px;
  color: ${props => props.$disabled ? COLOR_SYSTEM.get("Gray")?.[400] : COLOR_SYSTEM.get("Gray")?.[700]};
  padding: 8px;
  border-radius: 6px;
  transition: background-color 0.2s;
  opacity: ${props => props.$disabled ? 0.5 : 1};

  &:hover {
    background-color: ${props => props.$disabled ? 'transparent' : COLOR_SYSTEM.get("Gray")?.[50]};
  }

  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  }
`;

// 비활성 사유(예: "수집된 처방 없음") — 체크박스 라벨 옆에 표시한다(계획 item 13 · AC-31).
const DisabledReason = styled.span`
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Gray")?.[400]};
  font-weight: 400;
`;

/* 체크박스 라벨 + 부속 컨트롤(접기 토글·? 아이콘)을 한 줄에 배치하는 행.
   HTML 스펙상 label 안에는 해당 컨트롤 외 인터랙티브 요소를 둘 수 없어 형제로 분리한다 */
const LabelRow = styled.div`
  display: flex;
  align-items: center;

  ${'' /* 라벨이 남은 폭을 차지해 클릭 영역·hover 배경이 기존과 동일하게 유지된다 */}
  label {
    flex: 1;
  }
`;

// ? 아이콘 래퍼 (label 밖 형제)
const ScopeInfoWrap = styled.span`
  display: inline-flex;
  align-items: center;
`;

// 옵션 라벨 속 "종료 예상" 배지 실물 미리보기 — 표 안 배지는 이름 뒤에 붙어 margin-left가 있지만
// 라벨에서는 체크박스 뒤 첫 요소라 제거한다
const BadgePreview = styled(EndedDrugBadge)`
  margin-left: 0;
`;

// ? 툴팁의 제목 줄
const TooltipTitle = styled.div`
  margin-bottom: 4px;
  font-weight: 600;
`;

// ? 툴팁의 약품 항목 — 긴 약품명이 한 덩어리로 떨어져 "·"만 남는 줄바꿈 방지:
// 행잉 인덴트로 ·와 첫 글자를 붙이고, 이어지는 줄은 · 아래가 아닌 본문 위치로 들여쓴다.
// break-all로 공백 없는 긴 제품명도 줄 폭에 맞춰 쪼갠다
const TooltipDrugItem = styled.div`
  padding-left: 12px;
  text-indent: -12px;
  word-break: break-all;
`;

/**
 * ── 출력 설정 스타일 규격 (단일 출처) ──
 * · 들여쓰기 1단계 = 24px (하위 박스 margin-left, 박스 내 하위 라디오 padding-left)
 * · 상위 요소 → 하위 박스 간격 = 8px (flex gap 컨텍스트면 부모 gap이, 아니면 margin-top이 담당)
 * · 하위 박스: 테두리 Gray200 · 라운드 8px · 패딩 8px 10px
 * · 라벨: 박스 밖 14px/8px, 박스 안 13px/4px 8px, 가로 1줄 변형 13px/4px 4px(폭 제약)
 */
const subGroupBoxCss = css`
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 8px;
  padding: 8px 10px;
  margin-left: 24px;
`;

// "처방전 정보" 라벨 옆 세부 설정 접기/펼치기 토글 — label 클릭(체크박스 토글)과 분리
const DetailsToggle = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px 4px;
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  border-radius: 4px;

  &:hover {
    background: ${COLOR_SYSTEM.get("Gray")?.[100]};
  }
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RadioLabel = styled.label<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  font-size: 14px;
  color: ${props => props.$disabled ? COLOR_SYSTEM.get("Gray")?.[400] : COLOR_SYSTEM.get("Gray")?.[700]};
  padding: 8px;
  border-radius: 6px;
  transition: background-color 0.2s;
  opacity: ${props => props.$disabled ? 0.5 : 1};

  &:hover {
    background-color: ${props => props.$disabled ? 'transparent' : COLOR_SYSTEM.get("Gray")?.[50]};
  }

  input[type="radio"] {
    width: 16px;
    height: 16px;
    cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  }
`;

// "보고서 언급 약 추가" 하위 묶음 — 복용중 라디오의 자식임을 테두리로 명시.
// flex gap이 없는 세부설정 박스 내부라 상위 간격 8px를 margin-top으로 직접 준다
const MentionedSubGroup = styled.div`
  ${subGroupBoxCss}
  margin-top: 8px;

  /* 표시 방식(약만 간단히/처방전 통째) 라디오 — 체크박스의 하위이므로 1단계 들여쓰기 */
  ${RadioGroup} {
    padding-left: 24px;
  }
`;

const PrescriptionOptionsGroup = styled.div<{ $disabled?: boolean }>`
  background-color: ${COLOR_SYSTEM.get("Gray")?.[0]};
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 8px;
  padding: 12px;
  margin-left: 24px;
  opacity: ${props => props.$disabled ? 0.5 : 1};
  pointer-events: ${props => props.$disabled ? 'none' : 'auto'};
  transition: opacity 0.2s;

  /* 내부 요소들의 크기와 간격 줄이기 */
  ${CheckboxLabel} {
    font-size: 13px;
    padding: 4px 8px;
  }

  ${RadioLabel} {
    font-size: 13px;
    padding: 4px 8px;
  }

  ${RadioGroup} {
    gap: 4px;
  }
`;

const SubOptionLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  margin-bottom: 8px;
`;

/* 세부 설정 내부 섹션 간 간격 — 모든 섹션 사이 동일 적용 */
const SubOptionDivider = styled.div`
  height: 10px;
`;

// 환자용 복약지도문 하위: 의약품 카드(사진·성상·각인) 크기 — 좁은 패널이라 가로 1줄 라디오.
// 상위 간격 8px는 부모 OptionGroup의 flex gap이 제공 — margin-top을 주면 중복된다
const CardSizeGroup = styled.div<{ $disabled?: boolean }>`
  ${subGroupBoxCss}
  opacity: ${props => props.$disabled ? 0.5 : 1};
  pointer-events: ${props => props.$disabled ? 'none' : 'auto'};
`;

// 좁은 패널용 가로 1줄 라디오 (카드 크기·분류 표기 공용)
// 폭이 빠듯하면 넘치는 대신 줄바꿈 — 여백 최소화 + 고른 분배
const InlineRadioRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: flex-start;

  ${RadioLabel} {
    font-size: 13px;
    padding: 4px 4px;
    gap: 4px;
  }
`;

const PanelFooter = styled.div`
  padding: 20px;
  border-top: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PrintButton = styled(Button)`
  width: 100%;
  justify-content: center;
  gap: 8px;
`;

const PrintNotice = styled.p`
  font-size: 12px;
  line-height: 1.5;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
  margin: 0;
`;

/* ── 접이식(collapsible) 전용 — 탑재뷰에서만 쓴다. 약사 화면(collapsible=false)은 이 래퍼를 타지 않는다. ── */

// 우측 도킹 셸: [펼침 패널 240][레일 48]. fixed라 iframe 뷰포트 우측에 붙는다. 인쇄에서 숨긴다(AC-34·54).
const PanelDock = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  height: 100%;
  display: flex;
  z-index: 100;

  @media print {
    display: none;
  }
`;

const ExpandedPanel = styled.div`
  width: 240px;
  height: 100%;
  background: #fff;
  border-left: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.05);
  overflow: hidden;
`;

const Rail = styled.div`
  width: 48px;
  height: 100%;
  background: #fff;
  border-left: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding-top: 12px;
`;

interface PrintSidePanelProps {
  printOptions: PrintOptions;
  onOptionChange: (option: keyof PrintOptions, value: any) => void;
  onPrint: () => void;
  sectionExistence: {
    hasPharmacistReport: boolean;
    hasPatientGuide: boolean;
    isStaffGuide: boolean;
  };
  /**
   * 접이식 레일/패널로 렌더한다(탑재뷰 전용). 기본 false — 약사 화면은 현행 그대로 전체 패널.
   * 아래 신규 prop은 전부 선택적이라 기존 호출부(AIReviewPrintTemplate/index.tsx)가 깨지지 않는다.
   */
  collapsible?: boolean;
  /** 첫 진입 펼침 여부(collapsible일 때만). 탑재뷰는 저장된 값 또는 접힘(false)을 넘긴다. */
  defaultOpen?: boolean;
  /** 펼침/접힘이 바뀔 때 호출(탑재뷰가 localStorage 저장·레이아웃 push/overlay에 쓴다). */
  onToggle?: (open: boolean) => void;
  /** 비활성 사유. 있으면 해당 체크박스·프리셋을 비활성화하고 사유 문자열을 옆에 표시한다(§5.5). */
  disabledReasons?: { patientInfo?: string; prescriptionTable?: string };
  /**
   * 복용약 범위 UI(전체/복용중/보고서 언급) 노출 + 프리셋의 범위 연동 활성화. 기본 false(안전) —
   * 약사 인쇄 화면과 케어링노트 탑재뷰 모두 true를 넘긴다(탑재뷰 전체 노출, 지니님 확정 2026-09-07).
   * 설계: docs/ai-discussions/20260904-print-drug-scope-filter.md
   */
  showDrugScope?: boolean;
  /** "보고서 언급으로 추가될 약" 목록(복용중 제외·품목 dedupe) — "N종 추가" 라벨·툴팁에 표시 */
  mentionedAdditions?: MentionedAddition[];
  /**
   * 옵션별 "효과 있음" 판정 — false인 옵션은 비활성+사유 표시(켜도 지면이 안 바뀌는 혼란 방지).
   * 미전달이면 전부 유효로 취급한다(방어 기본값).
   */
  optionAvailability?: {
    hasEndedDrugs?: boolean;
    hasMeftCodes?: boolean;
    hasAtcCodes?: boolean;
    hasDrugCards?: boolean;
  };
}

const PrintSidePanel: React.FC<PrintSidePanelProps> = ({
  printOptions,
  onOptionChange,
  onPrint,
  sectionExistence,
  collapsible = false,
  defaultOpen = true,
  onToggle,
  disabledReasons,
  showDrugScope = false,
  mentionedAdditions = [],
  optionAvailability,
}) => {
  // 미전달이면 전부 유효 (방어 기본값)
  const avail = {
    hasEndedDrugs: optionAvailability?.hasEndedDrugs ?? true,
    hasMeftCodes: optionAvailability?.hasMeftCodes ?? true,
    hasAtcCodes: optionAvailability?.hasAtcCodes ?? true,
    hasDrugCards: optionAvailability?.hasDrugCards ?? true,
  };
  // 현재 선택된 프리셋 상태 관리
  const [currentPreset, setCurrentPreset] = useState<'pharmacist' | 'patient' | 'custom'>('custom');
  // 접이식 펼침 상태(collapsible일 때만 의미). 초기값은 defaultOpen.
  const [open, setOpen] = useState<boolean>(defaultOpen);
  // "처방전 세부 설정" 접기/펼치기 — 세션 로컬, 진입 시 항상 펼침(지니님 확정: 비저장)
  const [detailsOpen, setDetailsOpen] = useState<boolean>(true);

  const patientInfoDisabled = !!disabledReasons?.patientInfo;
  const prescriptionDisabled = !!disabledReasons?.prescriptionTable;

  const toggleOpen = () => {
    // 부작용(onToggle)을 setState 업데이터 밖에서 호출한다 — 업데이터 안에 두면 StrictMode 개발
    // 모드에서 업데이터가 두 번 불려 onToggle이 중복 발화한다.
    const next = !open;
    setOpen(next);
    onToggle?.(next);
  };

  // 개별 옵션 변경 시 커스텀 모드로 전환
  const handleOptionChange = (option: keyof PrintOptions, value: any) => {
    setCurrentPreset('custom');
    onOptionChange(option, value);
  };

  // 약사용 프리셋
  const applyPharmacistPreset = () => {
    if (prescriptionDisabled) return;
    setCurrentPreset('pharmacist');
    onOptionChange('showPatientInfo', true);
    onOptionChange('showPrescriptionTable', true);
    onOptionChange('prescriptionViewMode', 'hospital');
    onOptionChange('showDrugImages', false);
    onOptionChange('hidePharmacistReport', false);
    onOptionChange('hidePatientGuide', true);
    // 범위·표시 연동(설계 §4, 2026-09-05 개정): 약사용 = 복용중 + 언급 약 처방전 통째(병용약 맥락).
    // 청중 민감 옵션까지 스냅샷으로 잡는다 — 어떤 저장값이 남아 있든 프리셋 1회로 예측 가능한 출력.
    // 범위 UI가 없는 화면(showDrugScope=false)에서는 건드리지 않는다
    if (showDrugScope) {
      onOptionChange('drugScope', 'ongoing');
      // 추가할 언급 약이 없으면 체크만 남는 어색한 상태를 피한다
      onOptionChange('drugScopeAddMentioned', mentionedAdditions.length > 0);
      onOptionChange('drugScopeMentionedStyle', 'group');
      onOptionChange('markEndedDrugs', true);
      onOptionChange('classificationDisplay', 'meft'); // 약효군 시인성 → 치료중복 지면 확인
      onOptionChange('patientGuideCardSize', 'small'); // 성상·각인은 아는 정보 — 지면 절약
    }
  };

  // 환자용 프리셋
  const applyPatientPreset = () => {
    if (prescriptionDisabled) return;
    setCurrentPreset('patient');
    onOptionChange('showPatientInfo', false);
    onOptionChange('showPrescriptionTable', true);
    onOptionChange('prescriptionViewMode', 'date');
    onOptionChange('showDrugImages', true);
    onOptionChange('hidePharmacistReport', true);
    onOptionChange('hidePatientGuide', false);
    // 범위·표시 연동(설계 §4, 2026-09-05 개정): 환자용 = 복용중 + 언급 약 소표(약만 간단히).
    // 분류 표기·전문 코드는 환자에게 노이즈 → 끔. 카드는 환자 가독성 위해 크게
    if (showDrugScope) {
      onOptionChange('drugScope', 'ongoing');
      onOptionChange('drugScopeAddMentioned', mentionedAdditions.length > 0);
      onOptionChange('drugScopeMentionedStyle', 'drug');
      onOptionChange('markEndedDrugs', true);
      onOptionChange('classificationDisplay', 'none');
      onOptionChange('patientGuideCardSize', 'large');
    }
  };

  // 섹션 구분 여부 확인
  const hasSections = sectionExistence.hasPharmacistReport || sectionExistence.hasPatientGuide;

  // 환자용 자리(patient-side) 섹션 라벨: 종사자용 안내문이면 동적으로 변경
  const patientGuideLabel = sectionExistence.isStaffGuide ? '종사자용 안내문' : '환자용 복약지도문';

  const panelInner = (
    <>
      <PanelHeader>
        <Title>출력 설정</Title>
        <HeaderDescription>
          {hasSections
            ? "원하시는 항목을 직접 체크하시거나, 빠른 선택 버튼을 누르면 용도에 맞게 자동으로 설정됩니다. 자동 설정 후에도 수정 가능합니다."
            : "아래 옵션을 설정하여 출력하세요. (참고: 이 보고서는 약사용/환자용 구분이 없는 기존 형식입니다. 구분된 형식을 원하시면 보고서를 새로 생성해주세요.)"}
        </HeaderDescription>
      </PanelHeader>

      <PanelBody>
        {hasSections && (
          <PresetSection>
            <SectionLabel $variant="preset">빠른 선택</SectionLabel>
            <PresetButtonGroup $vertical={collapsible}>
              <PresetButton
                size="Small"
                type="Text"
                variant="Outlined"
                color={currentPreset === 'pharmacist' ? 'Primary' : 'Secondary'}
                disabled={prescriptionDisabled}
                onClick={applyPharmacistPreset}
              >
                약사용 보고서 출력
              </PresetButton>
              <PresetButton
                size="Small"
                type="Text"
                variant="Outlined"
                color={currentPreset === 'patient' ? 'Primary' : 'Secondary'}
                disabled={prescriptionDisabled}
                onClick={applyPatientPreset}
              >
                {patientGuideLabel} 출력
              </PresetButton>
            </PresetButtonGroup>
          </PresetSection>
        )}

        <OptionSection>
          <OptionTitle>포함할 항목</OptionTitle>
          <OptionGroup>
            <CheckboxLabel $disabled={patientInfoDisabled}>
              <input
                type="checkbox"
                checked={printOptions.showPatientInfo}
                onChange={(e) => handleOptionChange('showPatientInfo', e.target.checked)}
                disabled={patientInfoDisabled}
              />
              환자 정보
              {patientInfoDisabled && disabledReasons?.patientInfo && (
                <DisabledReason>({disabledReasons.patientInfo})</DisabledReason>
              )}
            </CheckboxLabel>
            <LabelRow>
            <CheckboxLabel $disabled={prescriptionDisabled}>
              <input
                type="checkbox"
                checked={printOptions.showPrescriptionTable}
                onChange={(e) => handleOptionChange('showPrescriptionTable', e.target.checked)}
                disabled={prescriptionDisabled}
              />
              처방전 정보
              {prescriptionDisabled && disabledReasons?.prescriptionTable && (
                <DisabledReason>({disabledReasons.prescriptionTable})</DisabledReason>
              )}
            </CheckboxLabel>
              {/* 신규 UI 공통 게이트 — showDrugScope 미전달 소비자에는 노출하지 않는다 */}
              {showDrugScope && (
                <DetailsToggle
                  type="button"
                  aria-expanded={detailsOpen}
                  aria-label="처방전 세부 설정 접기/펼치기"
                  onClick={() => setDetailsOpen((prev) => !prev)}
                >
                  <Icon
                    name={detailsOpen ? 'Caret Up' : 'Caret Down'}
                    size={14}
                    color={COLOR_SYSTEM.get("Gray")?.[500]}
                  />
                </DetailsToggle>
              )}
            </LabelRow>

            {detailsOpen && (
            <PrescriptionOptionsGroup
              $disabled={!printOptions.showPrescriptionTable}
              role="group"
              aria-labelledby="prescription-details-label"
              aria-disabled={!printOptions.showPrescriptionTable}
            >
              <SubOptionLabel id="prescription-details-label">처방전 세부 설정</SubOptionLabel>

              <CheckboxLabel $disabled={!printOptions.showPrescriptionTable}>
                <input
                  type="checkbox"
                  checked={printOptions.showDrugImages}
                  onChange={(e) => handleOptionChange('showDrugImages', e.target.checked)}
                  disabled={!printOptions.showPrescriptionTable}
                />
                의약품 이미지 표시
              </CheckboxLabel>

              {showDrugScope && (() => {
                const endedDisabled = !printOptions.showPrescriptionTable || !avail.hasEndedDrugs;
                return (
                  <CheckboxLabel $disabled={endedDisabled}>
                    <input
                      type="checkbox"
                      checked={printOptions.markEndedDrugs}
                      onChange={(e) => handleOptionChange('markEndedDrugs', e.target.checked)}
                      disabled={endedDisabled}
                    />
                    <BadgePreview>종료 예상</BadgePreview> 표시
                    {!avail.hasEndedDrugs && <DisabledReason>(해당 약 없음)</DisabledReason>}
                  </CheckboxLabel>
                );
              })()}

              <SubOptionDivider />

              <SubOptionLabel>정렬 방식</SubOptionLabel>
              <RadioGroup>
                <RadioLabel $disabled={!printOptions.showPrescriptionTable}>
                  <input
                    type="radio"
                    name="prescriptionViewMode"
                    value="date"
                    checked={printOptions.prescriptionViewMode === 'date'}
                    onChange={() => handleOptionChange('prescriptionViewMode', 'date')}
                    disabled={!printOptions.showPrescriptionTable}
                  />
                  날짜별 보기
                </RadioLabel>
                <RadioLabel $disabled={!printOptions.showPrescriptionTable}>
                  <input
                    type="radio"
                    name="prescriptionViewMode"
                    value="hospital"
                    checked={printOptions.prescriptionViewMode === 'hospital'}
                    onChange={() => handleOptionChange('prescriptionViewMode', 'hospital')}
                    disabled={!printOptions.showPrescriptionTable}
                  />
                  병원별 보기
                </RadioLabel>
              </RadioGroup>

              {showDrugScope && (
                <>
                  <SubOptionDivider />
                  <SubOptionLabel>분류 표기</SubOptionLabel>
                  <InlineRadioRow>
                    {(
                      [
                        ['none', '없음', true],
                        ['meft', '약효분류', avail.hasMeftCodes],
                        ['atc', 'ATC', avail.hasAtcCodes],
                      ] as const
                    ).map(([value, label, hasData]) => {
                      const optionDisabled = !printOptions.showPrescriptionTable || !hasData;
                      return (
                        <RadioLabel
                          key={value}
                          $disabled={optionDisabled}
                          title={!hasData ? '이 환자의 처방에는 해당 분류 정보가 없습니다' : undefined}
                        >
                          <input
                            type="radio"
                            name="classificationDisplay"
                            value={value}
                            checked={printOptions.classificationDisplay === value}
                            onChange={() => handleOptionChange('classificationDisplay', value)}
                            disabled={optionDisabled}
                          />
                          {label}
                        </RadioLabel>
                      );
                    })}
                  </InlineRadioRow>
                </>
              )}

              {showDrugScope && (() => {
                const tableOff = !printOptions.showPrescriptionTable;
                const isAll = printOptions.drugScope === 'all';
                const additionsCount = mentionedAdditions.length;
                // 언급 추가는 "현재 복용중인 약"의 부가 옵션 — 전체 처방약이거나 추가할 약이 없으면 비활성
                const mentionedDisabled = tableOff || isAll || additionsCount === 0;

                return (
                  <>
                    <SubOptionDivider />
                    <SubOptionLabel>복용약 범위</SubOptionLabel>
                    <RadioGroup>
                      <RadioLabel $disabled={tableOff}>
                        <input
                          type="radio"
                          name="drugScope"
                          value="all"
                          checked={isAll}
                          onChange={() => handleOptionChange('drugScope', 'all')}
                          disabled={tableOff}
                        />
                        전체 처방약
                      </RadioLabel>
                      <RadioLabel $disabled={tableOff}>
                        <input
                          type="radio"
                          name="drugScope"
                          value="ongoing"
                          checked={!isAll}
                          onChange={() => handleOptionChange('drugScope', 'ongoing')}
                          disabled={tableOff}
                        />
                        복용중인 약 (오늘 기준)
                      </RadioLabel>
                    </RadioGroup>
                    <MentionedSubGroup>
                    <LabelRow>
                    <CheckboxLabel $disabled={mentionedDisabled}>
                      <input
                        type="checkbox"
                        checked={!isAll && printOptions.drugScopeAddMentioned}
                        onChange={(e) => handleOptionChange('drugScopeAddMentioned', e.target.checked)}
                        disabled={mentionedDisabled}
                      />
                      보고서 언급 약 {additionsCount}종 추가
                    </CheckboxLabel>
                      {additionsCount > 0 && (
                        <ScopeInfoWrap data-testid="scope-mentioned-info">
                          <InfoIconButton
                            tooltip={
                              <div>
                                <TooltipTitle>보고서에 언급된 다음 약을 목록에 추가합니다.</TooltipTitle>
                                {mentionedAdditions.map((a) => (
                                  <TooltipDrugItem key={a.itemCode}>· {a.medicationName}</TooltipDrugItem>
                                ))}
                              </div>
                            }
                            iconName="Information Circle"
                            iconSize={14}
                            iconColor={COLOR_SYSTEM.get("Gray")?.[400]}
                            // 패널이 창 우측 끝에 있어 bottom(커서 우하단 펼침)은 뷰포트 밖으로 잘린다
                            // → 커서 왼쪽으로 펼쳐 본문 영역 위에 뜨게 한다
                            tooltipPosition="left"
                            tooltipMaxWidth="260px"
                          />
                        </ScopeInfoWrap>
                      )}
                    </LabelRow>
                    {(() => {
                      // 표시 방식 — 체크박스가 켜졌을 때만 활성 (약사는 병용약 맥락으로 용도를
                      // 추정하므로 "처방전 통째"가 필요할 수 있다. 지니님 확정, 2026-09-05)
                      const styleDisabled =
                        mentionedDisabled || !printOptions.drugScopeAddMentioned || isAll;
                      return (
                        <RadioGroup>
                          <RadioLabel $disabled={styleDisabled}>
                            <input
                              type="radio"
                              name="drugScopeMentionedStyle"
                              value="drug"
                              checked={printOptions.drugScopeMentionedStyle === 'drug'}
                              onChange={() => handleOptionChange('drugScopeMentionedStyle', 'drug')}
                              disabled={styleDisabled}
                            />
                            약만 간단히
                          </RadioLabel>
                          <RadioLabel $disabled={styleDisabled}>
                            <input
                              type="radio"
                              name="drugScopeMentionedStyle"
                              value="group"
                              checked={printOptions.drugScopeMentionedStyle === 'group'}
                              onChange={() => handleOptionChange('drugScopeMentionedStyle', 'group')}
                              disabled={styleDisabled}
                            />
                            처방전 통째
                          </RadioLabel>
                        </RadioGroup>
                      );
                    })()}
                    </MentionedSubGroup>
                  </>
                );
              })()}
            </PrescriptionOptionsGroup>
            )}

            {sectionExistence.hasPharmacistReport && (
              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={!printOptions.hidePharmacistReport}
                  onChange={(e) => handleOptionChange('hidePharmacistReport', !e.target.checked)}
                />
                약사용 보고서
              </CheckboxLabel>
            )}
            {sectionExistence.hasPatientGuide && (
              <>
                <CheckboxLabel>
                  <input
                    type="checkbox"
                    checked={!printOptions.hidePatientGuide}
                    onChange={(e) => handleOptionChange('hidePatientGuide', !e.target.checked)}
                  />
                  {patientGuideLabel}
                </CheckboxLabel>
                {showDrugScope && (
                  <CardSizeGroup
                    data-testid="card-size-group"
                    $disabled={printOptions.hidePatientGuide || !avail.hasDrugCards}
                  >
                    <SubOptionLabel>
                      의약품 카드 크기
                      {!avail.hasDrugCards && <DisabledReason> (보고서에 카드 없음)</DisabledReason>}
                    </SubOptionLabel>
                    <InlineRadioRow>
                      {(
                        [
                          ['none', '없음'],
                          ['small', '작게'],
                          ['medium', '보통'],
                          ['large', '크게'],
                        ] as const
                      ).map(([value, label]) => (
                        <RadioLabel key={value} $disabled={printOptions.hidePatientGuide}>
                          <input
                            type="radio"
                            name="patientGuideCardSize"
                            value={value}
                            checked={printOptions.patientGuideCardSize === value}
                            onChange={() => handleOptionChange('patientGuideCardSize', value)}
                            disabled={printOptions.hidePatientGuide || !avail.hasDrugCards}
                          />
                          {label}
                        </RadioLabel>
                      ))}
                    </InlineRadioRow>
                  </CardSizeGroup>
                )}
              </>
            )}
          </OptionGroup>
        </OptionSection>
      </PanelBody>

      <PanelFooter>
        <PrintNotice>
          인쇄 시 각 주요 섹션(환자 정보, 처방 정보, 약사용 보고서, {patientGuideLabel})은 새 페이지에서 시작됩니다.
          <br />
          의약품 카드의 제품명과 식별정보는 환자 가독성을 위해 인쇄 시 확대됩니다.
        </PrintNotice>
        <PrintButton
          size="Medium"
          type="IconWithText"
          variant="Contained"
          color="Primary"
          left
          icon="Print"
          onClick={onPrint}
        >
          인쇄하기
        </PrintButton>
      </PanelFooter>
    </>
  );

  // 약사 화면(비접이식) — 현행 그대로. SidePanel 래퍼가 폭·인쇄 은닉을 담당한다.
  if (!collapsible) {
    return <PanelContainer>{panelInner}</PanelContainer>;
  }

  // 탑재뷰(접이식) — 우측 도킹. 접힘: 레일(설정 토글 + 인쇄). 펼침: 패널(설정=닫기) + 푸터 인쇄하기.
  // 인쇄 버튼은 어느 상태에서도 정확히 1개(접힘=레일, 펼침=푸터) — 공존하지 않는다(AC-68·69).
  return (
    <PanelDock>
      {open && (
        <ExpandedPanel>
          <PanelContainer>{panelInner}</PanelContainer>
        </ExpandedPanel>
      )}
      <Rail>
        <Button
          type="Icon"
          size="Medium"
          variant="Ghost"
          color="Secondary"
          // 'Setting 01' 에셋은 레터폼 플레이스홀더(viewBox 0 0 65 15 — 'settings-01' 글자)라 톱니가
          // 아니라 글자로 렌더된다. 'Setting 02'(슬라이더, 24×24 outline)를 쓴다 — 옆 인쇄 아이콘의
          // Ghost outline과 톤이 맞고 '출력 설정=옵션 조절'에 의미상 맞다. 회귀 방지: iconAssets.test.ts.
          icon={open ? 'X' : 'Setting 02'}
          title="출력 설정"
          onClick={toggleOpen}
        />
        {!open && (
          <Button
            type="Icon"
            size="Medium"
            variant="Ghost"
            color="Primary"
            icon="Print"
            title="인쇄"
            onClick={onPrint}
          />
        )}
      </Rail>
    </PanelDock>
  );
};

export default PrintSidePanel;
